import type { DriveStep } from 'driver.js';

/** Route that must be active for this step's element to exist. */
export const TUTORIAL_ROUTES: Record<string, string> = {
  dashboard: '/dashboard',
  assets: '/assets',
  api: '/api',
  myAccount: '/my-account',
  billing: '/billing',
  resources: '/resources',
};

export interface TutorialStepConfig extends DriveStep {
  /** Unique id used by tutorial registry (matches data-tutorial value). */
  id: string;
  /** Route path for steps that live on another page */
  route?: string;
}

/**
 * All steps keyed by id. Targets use [data-tutorial="..."] selectors.
 *
 * Icon/button steps (small controls) get a blue box highlight; see TutorialFiltersBlueBox
 * (stepId → selector map) and TutorialCombinedHighlight (trigger + content union steps).
 */
const steps: TutorialStepConfig[] = [
  {
    id: 'first-view-card',
    element: '[data-tutorial="first-view-card"]',
    popover: {
      title: 'View cards',
      description: 'This is your first view card. Views can contain multiple charts and visualizations that reflect your current filter and date range.',
      side: 'bottom',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'card-info-icon',
    element: '[data-tutorial="card-info-icon"]',
    popover: {
      title: 'Info icon',
      description: 'Every card has an info icon. Click it to learn what the card does and how to configure it.',
      side: 'left',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'card-config-icon',
    element: '[data-tutorial="card-config-combined-highlight"]',
    popover: {
      title: 'Config icon',
      description: 'All cards have a config (settings) icon. Use it to customize the card—change attributes, title, and other options. The settings panel is open here so you can see it.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'view-actions-menu',
    element: '[data-tutorial="view-actions-combined-highlight"]',
    popover: {
      title: 'View actions',
      description: 'Use this menu to save, update, clear, or delete the current view. Your changes are saved to your account.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'add-viz-menu',
    element: '[data-tutorial="add-viz-combined-highlight"]',
    popover: {
      title: 'Add Visualization',
      description: 'Click here to add new cards to your view—pie charts, bar charts, records lists, node graphs, and more.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'load-view-menu',
    element: '[data-tutorial="load-view-combined-highlight"]',
    popover: {
      title: 'Load View',
      description: 'Open this menu to search and load a saved view. Switch between different dashboard layouts you\'ve created.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'refresh-view-menu',
    element: '[data-tutorial="refresh-view-combined-highlight"]',
    popover: {
      title: 'Refresh View',
      description: 'Refresh the view data now or set an auto-refresh interval so your cards stay up to date.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'create-new-tab',
    element: '[data-tutorial="create-new-tab"]',
    popover: {
      title: 'Create New Tab',
      description: 'Create a new view or a full-screen node graph. Each tab can have its own set of cards and visualizations.',
      side: 'bottom',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'ai-input',
    element: '[data-tutorial="ai-input"]',
    popover: {
      title: 'AI Assistant input',
      description: 'Type your questions here about security threats, creating filters, or how to use the platform. Hit the green button to send.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'ai-send',
    element: '[data-tutorial="ai-send"]',
    popover: {
      title: 'Send',
      description: 'Click to send your message to the AI Assistant.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'ai-example-prompts',
    element: '[data-tutorial="ai-example-prompts"]',
    popover: {
      title: 'Example prompts',
      description: 'Try these example prompts to get started. The AI can search data and help you build filters.',
      side: 'top',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'ai-panel-toggle',
    element: '[data-tutorial="ai-panel-toggle"]',
    popover: {
      title: 'AI Assistant panel',
      description: 'Use this icon to open or close the AI Assistant pane. The panel stays available across all pages.',
      side: 'bottom',
      align: 'end',
    },
    route: '/dashboard',
  },
  {
    id: 'record-count',
    element: '[data-tutorial="record-count"]',
    popover: {
      title: 'Record Count',
      description: 'This shows how many products (records) match your current filter and date range.',
      side: 'bottom',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'tag-count',
    element: '[data-tutorial="tag-count"]',
    popover: {
      title: 'Tag Count',
      description: 'This shows the number of tags in the filtered results. Use it to see how much tagged data you have in the selected range.',
      side: 'bottom',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-tile',
    element: '[data-tutorial="date-range-tile"]',
    popover: {
      title: 'Date Range',
      description: 'This tile controls the time window for your data. Click the date to open the picker and choose quick presets or a custom range. The maximum range is 7 days.',
      side: 'bottom',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-trigger',
    element: '[data-tutorial="date-range-trigger-combined-highlight"]',
    popover: {
      title: 'Open date picker',
      description: 'Click here to open the date range picker and change the time window.',
      side: 'bottom',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-mode-toggle',
    element: '[data-tutorial="date-range-mode-toggle"]',
    popover: {
      title: 'Quick Select or Custom Range',
      description: 'Use Quick Select for presets like Last 24 hours or Last 7 days. Use Custom Range to pick exact dates and times.',
      side: 'bottom',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-quick-presets',
    element: '[data-tutorial="date-range-quick-presets"]',
    popover: {
      title: 'Quick presets',
      description: 'Choose a preset to set the range. Your data will update to match the selected window.',
      side: 'bottom',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-custom-range',
    element: '[data-tutorial="date-range-popover"]',
    popover: {
      title: 'Custom range',
      description: 'Select start and end dates on the calendar, and optionally set start and end times (UTC).',
      side: 'top',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'date-range-apply',
    element: '[data-tutorial="date-range-apply"]',
    popover: {
      title: 'Apply or cancel',
      description: 'Click OK to apply the selected date range, or Close to cancel without changing.',
      side: 'top',
      align: 'end',
    },
    route: '/dashboard',
  },
  {
    id: 'filter-pane',
    element: '[data-tutorial="filter-pane"]',
    popover: {
      title: 'Filter pane',
      description: 'Expand the filter to narrow your data by category, source, industry, region, and more.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'filter-accordion-category',
    element: '[data-tutorial="filter-accordion-category"]',
    popover: {
      title: 'Category',
      description: 'Filter by product category (e.g. threat type, report type).',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'filter-accordion-source',
    element: '[data-tutorial="filter-accordion-source"]',
    popover: {
      title: 'Source',
      description: 'Filter by data source or publisher.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'filter-accordion-industry',
    element: '[data-tutorial="filter-accordion-industry"]',
    popover: {
      title: 'Industry',
      description: 'Filter by industry vertical (e.g. finance, healthcare).',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'filter-accordion-region',
    element: '[data-tutorial="filter-accordion-region"]',
    popover: {
      title: 'Region',
      description: 'Filter by geographic region.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'assets-tabs-row',
    element: '[data-tutorial="assets-tabs-row"]',
    popover: {
      title: 'Assets',
      description: 'As you build in Truss, you can manage your saved filters, views, node graphs, and reports from these tabs.',
      side: 'bottom',
      align: 'center',
    },
    route: '/assets',
  },
  {
    id: 'assets-filters-tab',
    element: '[data-tutorial="assets-filters-tab"]',
    popover: {
      title: 'Filters tab',
      description: 'View, edit, and delete your saved filters. Filters define which data appears in your views and reports.',
      side: 'bottom',
      align: 'center',
    },
    route: '/assets',
  },
  {
    id: 'assets-views-tab',
    element: '[data-tutorial="assets-views-tab"]',
    popover: {
      title: 'Views tab',
      description: 'Manage your saved dashboard views. Open, duplicate, or delete views you\'ve created.',
      side: 'bottom',
      align: 'center',
    },
    route: '/assets',
  },
  {
    id: 'assets-node-graphs-tab',
    element: '[data-tutorial="assets-node-graphs-tab"]',
    popover: {
      title: 'Node Graphs tab',
      description: 'Access your saved node graphs. Node graphs visualize relationships in your data.',
      side: 'bottom',
      align: 'center',
    },
    route: '/assets',
  },
  {
    id: 'assets-reports-tab',
    element: '[data-tutorial="assets-reports-tab"]',
    popover: {
      title: 'Reports tab',
      description: 'Manage your saved reports. Reports combine views and filters for sharing and export.',
      side: 'bottom',
      align: 'center',
    },
    route: '/assets',
  },
  {
    id: 'api-curl-example',
    element: '[data-tutorial="api-curl-example"]',
    popover: {
      title: 'API example',
      description: 'As you build filters for specific data, the API filter will dynamically adjust. Copy and paste this code to programmatically collect Truss data.',
      side: 'bottom',
      align: 'start',
    },
    route: '/api',
  },
  {
    id: 'api-languages',
    element: '[data-tutorial="api-languages"]',
    popover: {
      title: 'Language options',
      description: 'Use whatever language works best for you. The API supports curl, JavaScript, Python, Go, Rust, Ruby, and more.',
      side: 'bottom',
      align: 'start',
    },
    route: '/api',
  },
  {
    id: 'account-menu',
    element: '[data-tutorial="account-menu-combined-highlight"]',
    popover: {
      title: 'Account menu',
      description: 'Open this menu to access your Account, API Key, Billing, and Resources.',
      side: 'bottom',
      align: 'end',
    },
    route: '/dashboard',
  },
  {
    id: 'api-key-menu-link',
    element: '[data-tutorial="api-key-menu-link"]',
    popover: {
      title: 'API Key',
      description: 'Open the API Key page to view, copy, and learn how to use your key with the API, truss-agent, and truss-agent-mcp.',
      side: 'bottom',
      align: 'end',
    },
    route: '/dashboard',
  },
  {
    id: 'api-key-section',
    element: '[data-tutorial="api-key-section"]',
    popover: {
      title: 'Your API key',
      description: 'Every account includes an API key with tier-based daily limits. Hover to reveal your key and use the copy button to copy it.',
      side: 'right',
      align: 'start',
    },
    route: '/api-key',
  },
  {
    id: 'api-tab',
    element: '[data-tutorial="api-tab"]',
    popover: {
      title: 'API',
      description: 'Click the API link in the navigation to open the API page, where you can see code samples that use your API key.',
      side: 'bottom',
      align: 'start',
    },
    route: '/api-key',
  },
  {
    id: 'profile-section',
    element: '[data-tutorial="profile-section"]',
    popover: {
      title: 'Profile',
      description: 'Manage your username and bio here. Theme preferences are also on this page.',
      side: 'right',
      align: 'start',
    },
    route: '/my-account',
  },
  {
    id: 'theme-preference',
    element: '[data-tutorial="theme-preference"]',
    popover: {
      title: 'Theme preference',
      description: 'Switch between Light and Dark mode here.',
      side: 'left',
      align: 'start',
    },
    route: '/my-account',
  },
  {
    id: 'manage-subscription',
    element: '[data-tutorial="manage-subscription"]',
    popover: {
      title: 'Manage Subscription',
      description: 'Community is dashboard-only (free). Access ($200/mo) unlocks your API key, Truss Query Language (TQL), and Truss Agent automation. Deployment ($500/mo) adds the Truss MCP endpoint and Local MCP Agent for LLM integrations. Enterprise includes all features scaled for MSSP, MDR, and multi-team operations—upgrade or manage your subscription here.',
      side: 'top',
      align: 'center',
    },
    route: '/billing',
  },
  {
    id: 'resources-ask-share',
    element: '[data-tutorial="resources-ask-share"]',
    popover: {
      title: 'Resources',
      description: 'Documentation, API reference, and help center links are here. Use the Tutorials section below to dive deeper by topic.',
      side: 'top',
      align: 'center',
    },
    route: '/resources',
  },
  {
    id: 'join-community-discord',
    element: '[data-tutorial="join-community-discord"]',
    popover: {
      title: 'Join Our Community',
      description: 'Several resources are available for you to ask questions and share comments. Communicate with the Truss community through Discord.',
      side: 'top',
      align: 'center',
    },
    route: '/resources',
  },
  {
    id: 'tutorial-launch-pad',
    element: '[data-tutorial="tutorial-launch-pad"]',
    popover: {
      title: 'More tutorials',
      description: 'Choose a topic below to explore in depth. When you\'re done, you\'ll return to the dashboard.',
      side: 'top',
      align: 'center',
    },
    route: '/resources',
  },
  {
    id: 'node-graph-modal-create',
    element: '[data-tutorial="product-modal-create-node-graph"]',
    popover: {
      title: 'Create Node Graph',
      description:
        'This record (product ID 56806) is open as an example. Use Create Node Graph to open the same graph flow you would from any product—full product data is passed into the graph. Click Next when you are ready to continue.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-open',
    element: '[data-tutorial="node-graph-open-combined-highlight"]',
    popover: {
      title: 'Your node graph workspace',
      description:
        'This tab is your node graph for the product you opened—the same path as Create Node Graph in the product modal, with full product data driving the graph. The main canvas below is where nodes and edges are drawn: drag the background to pan, and use the rest of this tour to explore tools and the node tree.',
      side: 'left',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-zoom',
    element: '[data-tutorial="network-chart-zoom-controls"]',
    popover: {
      title: 'Zoom controls',
      description:
        'Use + to zoom in, − to zoom out, Reset to fit, and 100% to see the current zoom level. You can also zoom in and out with your scroll wheel.',
      side: 'left',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-tools',
    element: '[data-tutorial="network-chart-tools-panel"]',
    popover: {
      title: 'Tools',
      description:
        'The Tools column on the left groups everything for interacting with the graph: selection, layout, filtering, and a tree of all nodes.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-selection',
    element: '[data-tutorial="network-chart-tools-selection"]',
    popover: {
      title: 'Selection',
      description:
        'Select nodes on the graph, then use these controls to refine: siblings, parent, children, invert, select all children, the whole product cluster, or clear the selection.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-layout',
    element: '[data-tutorial="network-chart-tools-layout"]',
    popover: {
      title: 'Layout',
      description:
        'Recalculate layout runs the force simulation again. Unlock all nodes or lock selected nodes to fix positions while you edit. Force reset clears cached positions and starts fresh.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-filtering',
    element: '[data-tutorial="network-chart-tools-filtering"]',
    popover: {
      title: 'Filtering',
      description:
        'Merged attribute nodes can be checked here, then Apply filter adds those values to the global filter pane on the dashboard so the rest of the app matches your graph. Save filter stores the same criteria as a saved filter.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-nodes-tree',
    element: '[data-tutorial="network-chart-tools-nodes"]',
    popover: {
      title: 'Nodes',
      description:
        'A tree of every node in the graph—expand rows to see children. Click a row to select or deselect that node on the canvas.',
      side: 'right',
      align: 'start',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-context-search',
    element: '[data-tutorial="network-chart-tutorial-context-node-frame"]',
    popover: {
      title: 'Select a node',
      description:
        'A Tier 4 IOC leaf is selected and centered in the view. Right-click a node like this one to open its context menu—the next step shows the menu alongside the node.',
      side: 'top',
      align: 'center',
    },
    route: '/dashboard',
  },
  {
    id: 'node-graph-context-menu',
    element: '[data-tutorial="node-graph-context-menu-combined-highlight"]',
    popover: {
      title: 'Context menu',
      description:
        'The blue outline now includes the node and the right-click menu. Use actions here—for example Search for other products to find records that share that IOC or attribute.',
      side: 'left',
      align: 'center',
    },
    route: '/dashboard',
  },
];

/** Map step id -> step config for registry lookups */
export const STEP_BY_ID: Record<string, TutorialStepConfig> = Object.fromEntries(
  steps.map((s) => [s.id, s])
);

/** Legacy flat list (primary tutorial step order) for backward compatibility during migration. */
export const TUTORIAL_STEPS: TutorialStepConfig[] = steps;

export interface TutorialDef {
  id: string;
  title: string;
  description?: string;
  /** Ordered step ids (must exist in STEP_BY_ID). */
  stepIds: string[];
  /** Route to navigate to when starting this tutorial. */
  initialRoute: string;
  /** If true, DashboardPage expands filter pane for filter steps (e.g. filters tutorial). */
  expandFilterForSteps?: boolean;
}

/** Tutorial registry: id, title, step list, and initial route. */
export const TUTORIAL_REGISTRY: TutorialDef[] = [
  {
    id: 'primary',
    title: 'Dashboard Overview',
    description: 'A quick tour of the main areas on the dashboard.',
    stepIds: [
      'first-view-card',
      'add-viz-menu',
      'ai-input',
      'record-count',
      'date-range-tile',
      'filter-pane',
      'account-menu',
      'tutorial-launch-pad',
    ],
    initialRoute: '/dashboard',
  },
  {
    id: 'views-cards',
    title: 'Views & Cards',
    description: 'Build and manage view cards, add visualizations, and load saved views.',
    stepIds: [
      'first-view-card',
      'card-info-icon',
      'card-config-icon',
      'view-actions-menu',
      'add-viz-menu',
      'load-view-menu',
      'refresh-view-menu',
      'create-new-tab',
    ],
    initialRoute: '/dashboard',
  },
  {
    id: 'ai-assistant',
    title: 'AI Assistant Overview',
    description: 'Use the AI Assistant to search data and build filters.',
    stepIds: ['ai-input', 'ai-send', 'ai-example-prompts', 'ai-panel-toggle'],
    initialRoute: '/dashboard',
  },
  {
    id: 'filters',
    title: 'Filters & Date Range',
    description: 'Narrow your data with filters and the date range picker.',
    stepIds: [
      'tag-count',
      'record-count',
      'date-range-tile',
      'date-range-trigger',
      'date-range-mode-toggle',
      'date-range-quick-presets',
      'date-range-custom-range',
      'date-range-apply',
      'filter-pane',
      'filter-accordion-category',
      'filter-accordion-source',
      'filter-accordion-industry',
      'filter-accordion-region',
    ],
    initialRoute: '/dashboard',
    expandFilterForSteps: true,
  },
  {
    id: 'assets',
    title: 'Assets Overview',
    description: 'Manage your saved filters, views, node graphs, and reports.',
    stepIds: [
      'assets-tabs-row',
      'assets-filters-tab',
      'assets-views-tab',
      'assets-node-graphs-tab',
      'assets-reports-tab',
    ],
    initialRoute: '/assets',
  },
  {
    id: 'api',
    title: 'API Overview',
    description: 'Get your API key and use the API with filters.',
    stepIds: ['account-menu', 'api-key-menu-link', 'api-key-section', 'api-tab', 'api-curl-example', 'api-languages'],
    initialRoute: '/dashboard',
  },
  {
    id: 'account-settings',
    title: 'Account & Settings',
    description: 'Profile, theme, and billing.',
    stepIds: ['account-menu', 'profile-section', 'theme-preference', 'manage-subscription'],
    initialRoute: '/dashboard',
  },
  {
    id: 'node-graph',
    title: 'Node Graph Overview',
    description:
      'Create a graph from a product record, then explore the workspace, tools, and filters.',
    stepIds: [
      'node-graph-modal-create',
      'node-graph-open',
      'node-graph-zoom',
      'node-graph-tools',
      'node-graph-selection',
      'node-graph-layout',
      'node-graph-filtering',
      'node-graph-nodes-tree',
      'node-graph-context-search',
      'node-graph-context-menu',
    ],
    initialRoute: '/dashboard',
  },
];

/** Return ordered steps for a tutorial by id. */
export function getStepsForTutorial(tutorialId: string): TutorialStepConfig[] {
  const def = TUTORIAL_REGISTRY.find((t) => t.id === tutorialId);
  if (!def) return [];
  return def.stepIds
    .map((id) => STEP_BY_ID[id])
    .filter((s): s is TutorialStepConfig => s != null);
}

/** Step ids that require the filter pane to be expanded (for filters tutorial). */
export const FILTER_TUTORIAL_STEP_IDS = [
  'filter-pane',
  'filter-accordion-category',
  'filter-accordion-source',
  'filter-accordion-industry',
  'filter-accordion-region',
];

/** Step ids whose selectors live inside the AI Assistant right panel (panel must be mounted). */
export const AI_ASSISTANT_PANEL_CONTENT_STEP_IDS = [
  'ai-input',
  'ai-send',
  'ai-example-prompts',
] as const;

/** Demo product loaded for the Node Graph tutorial (Threat Studio tour). */
export const NODE_GRAPH_TUTORIAL_DEMO_PRODUCT_ID = 56806;

/** localStorage value: `{ "id": number }` — last product for optional “remember last graph” flows (modals, Create node graph). */
export const NODE_GRAPH_TUTORIAL_LAST_PRODUCT_KEY = 'truss-dashboard-node-graph-tutorial-last-product-id';

export function persistLastProductIdForNodeGraphTutorial(id: number): void {
  if (!Number.isFinite(id)) return;
  try {
    localStorage.setItem(NODE_GRAPH_TUTORIAL_LAST_PRODUCT_KEY, JSON.stringify({ id }));
  } catch {
    /* ignore */
  }
}
